import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../../api.js';
import { Package, X, Car, MapPin, Plus, Trash2 } from 'lucide-react';
import { useOrganizations } from '../../../index.js';

function ToolSetForm({ isOpen, onClose, onSuccess, onMessage, editingToolSet }) {
  const { t } = useTranslation();
  const { data: organizations = [] } = useOrganizations();

  // Fetch vehicles and locations for assignment
  const { data: vehicles = [] } = useQuery({
    queryKey: ['vehicles'],
    queryFn: async () => {
      const response = await api.vehicle.getAll();
      return response.data || [];
    },
  });

  const { data: workingSites = [] } = useQuery({
    queryKey: ['workingSites'],
    queryFn: async () => {
      const response = await api.workingSite.getAll();
      return response.data || [];
    },
  });

  // Fetch all available tools for adding to set
  const { data: availableTools = [] } = useQuery({
    queryKey: ['tools'],
    queryFn: async () => {
      const response = await api.tool.getAll();
      return response.data || [];
    },
  });

  // Basic Information
  const [orgId, setOrgId] = useState('');
  const [setCode, setSetCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Assignment
  const [assignedToType, setAssignedToType] = useState('unassigned');
  const [assignedToId, setAssignedToId] = useState('');

  // Tools in this set
  const [selectedTools, setSelectedTools] = useState([]);

  useEffect(() => {
    if (editingToolSet) {
      setOrgId(editingToolSet.org_id || '');
      setSetCode(editingToolSet.set_code || '');
      setName(editingToolSet.name || '');
      setDescription(editingToolSet.description || '');
      setAssignedToType(editingToolSet.assigned_to_type || 'unassigned');
      setAssignedToId(editingToolSet.assigned_to_id || '');
    } else {
      resetForm();
    }
  }, [editingToolSet, isOpen]);

  const resetForm = () => {
    setOrgId(organizations.length > 0 ? organizations[0].code : '');
    setSetCode('');
    setName('');
    setDescription('');
    setAssignedToType('unassigned');
    setAssignedToId('');
    setSelectedTools([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!orgId || !setCode || !name) {
      onMessage && onMessage({
        title: t('tools.toast.error'),
        message: t('tools.form.errors.requiredFields'),
        type: 'error'
      });
      return;
    }

    try {
      const toolSetData = {
        org_id: orgId,
        set_code: setCode,
        name: name,
        description: description || null,
        assigned_to_type: assignedToType,
        assigned_vehicle_id: assignedToType === 'vehicle' && assignedToId ? parseInt(assignedToId) : null,
        assigned_location_id: assignedToType === 'location' && assignedToId ? parseInt(assignedToId) : null,
      };

      if (editingToolSet) {
        // Update existing tool set
        await api.toolSet.update(editingToolSet.id, toolSetData);
        onMessage && onMessage({
          title: t('tools.toast.success'),
          message: t('tools.messages.setUpdated'),
          type: 'success'
        });
      } else {
        // Create new tool set
        const response = await api.toolSet.create(toolSetData);
        const newToolSetId = response.data?.id;

        // Create tool set items for selected tools
        if (selectedTools.length > 0 && newToolSetId) {
          for (const toolId of selectedTools) {
            try {
              await api.toolSetItem.create({
                org_id: orgId,
                tool_set_id: newToolSetId,
                tool_id: toolId,
              });
            } catch (error) {
              console.error('Error adding tool to set:', error);
            }
          }
        }

        onMessage && onMessage({
          title: t('tools.toast.success'),
          message: t('tools.messages.setCreated'),
          type: 'success'
        });
      }

      onSuccess && onSuccess();
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error submitting tool set:', error);
      onMessage && onMessage({
        title: t('tools.toast.error'),
        message: error.message || t('tools.messages.setSaveError'),
        type: 'error'
      });
    }
  };

  const addTool = (toolId) => {
    if (toolId && !selectedTools.includes(parseInt(toolId))) {
      setSelectedTools([...selectedTools, parseInt(toolId)]);
    }
  };

  const removeTool = (toolId) => {
    setSelectedTools(selectedTools.filter(id => id !== toolId));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Package className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">
                {editingToolSet ? t('tools.setForm.editTitle') : t('tools.setForm.createTitle')}
              </h2>
              <p className="text-sm text-amber-100">
                {editingToolSet
                  ? t('tools.setForm.editSubtitle', { name: editingToolSet.name })
                  : t('tools.setForm.createSubtitle')}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
              {t('tools.form.sections.basic')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.organization')} *
                </label>
                <select
                  value={orgId}
                  onChange={(e) => setOrgId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="">{t('tools.setForm.selectOrganization')}</option>
                  {organizations.map((org) => (
                    <option key={org.code} value={org.code}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.setForm.fields.setCode')} *
                </label>
                <input
                  type="text"
                  value={setCode}
                  onChange={(e) => setSetCode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder={t('tools.setForm.placeholders.setCode')}
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.setForm.fields.setName')} *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder={t('tools.setForm.placeholders.setName')}
                  required
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.description')}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder={t('tools.setForm.placeholders.description')}
                ></textarea>
              </div>
            </div>
          </div>

          {/* Assignment Section */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                {t('tools.form.sections.assignment')}
              </div>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('tools.form.fields.assignmentType')}
                </label>
                <select
                  value={assignedToType}
                  onChange={(e) => {
                    setAssignedToType(e.target.value);
                    setAssignedToId('');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="unassigned">{t('tools.assignment.unassigned')}</option>
                  <option value="vehicle">{t('tools.assignment.vehicle')}</option>
                  <option value="location">{t('tools.assignment.site')}</option>
                </select>
              </div>

              {assignedToType === 'vehicle' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center gap-1">
                      <Car className="w-4 h-4" />
                      {t('tools.assignment.vehicle')}
                    </div>
                  </label>
                  <select
                    value={assignedToId}
                    onChange={(e) => setAssignedToId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">{t('tools.assignment.selectVehicle')}</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.license_plate} - {vehicle.make} {vehicle.model}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {assignedToType === 'location' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {t('tools.assignment.site')}
                    </div>
                  </label>
                  <select
                    value={assignedToId}
                    onChange={(e) => setAssignedToId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">{t('tools.assignment.selectSite')}</option>
                    {workingSites.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name} ({site.city || t('common.states.notAvailable')})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Tools in Set - Dual Multi-Select (Only for new sets) */}
          {!editingToolSet && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 border-b pb-2">
                {t('tools.setForm.toolsInSet')}
              </h3>

              {/* Dual List Transfer Component */}
              <div className="grid grid-cols-[1fr,auto,1fr] gap-4 items-center">
                {/* Available Tools List */}
                <div className="border border-gray-300 rounded-lg">
                  <div className="bg-gray-100 px-4 py-2 border-b border-gray-300">
                    <p className="text-sm font-medium text-gray-700">
                      {t('tools.setForm.availableTools', {
                        n: availableTools.filter(tool => !selectedTools.includes(tool.id)).length
                      })}
                    </p>
                  </div>
                  <div className="h-64 overflow-y-auto p-2">
                    {availableTools
                      .filter(tool => !selectedTools.includes(tool.id))
                      .map((tool) => (
                        <div
                          key={tool.id}
                          onClick={() => addTool(tool.id)}
                          className="px-3 py-2 hover:bg-blue-100 cursor-pointer rounded mb-1 text-sm"
                        >
                          <div className="font-medium">{tool.name}</div>
                          <div className="text-xs text-gray-500">{tool.tool_code}</div>
                        </div>
                      ))}
                    {availableTools.filter(tool => !selectedTools.includes(tool.id)).length === 0 && (
                      <div className="text-center text-gray-500 text-sm py-8">
                        {t('tools.setForm.noAvailableTool')}
                      </div>
                    )}
                  </div>
                </div>

                {/* Transfer Buttons */}
                <div className="flex flex-col gap-2">
                  <div className="text-gray-400 text-2xl">→</div>
                  <div className="text-gray-400 text-2xl">←</div>
                </div>

                {/* Selected Tools List */}
                <div className="border border-gray-300 rounded-lg">
                  <div className="bg-blue-100 px-4 py-2 border-b border-gray-300">
                    <p className="text-sm font-medium text-gray-700">
                      {t('tools.setForm.selectedTools', { n: selectedTools.length })}
                    </p>
                  </div>
                  <div className="h-64 overflow-y-auto p-2">
                    {selectedTools.map(toolId => {
                      const tool = availableTools.find(t => t.id === toolId);
                      return tool ? (
                        <div
                          key={toolId}
                          onClick={() => removeTool(toolId)}
                          className="px-3 py-2 hover:bg-red-100 cursor-pointer rounded mb-1 text-sm"
                        >
                          <div className="font-medium">{tool.name}</div>
                          <div className="text-xs text-gray-500">{tool.tool_code}</div>
                        </div>
                      ) : null;
                    })}
                    {selectedTools.length === 0 && (
                      <div className="text-center text-gray-500 text-sm py-8">
                        {t('tools.setForm.noSelectedTool')}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500 mt-2 text-center">
                {t('tools.setForm.transferHint')}
              </p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('common.actions.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600"
          >
            {editingToolSet ? t('tools.form.actions.update') : t('tools.setForm.actions.create')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ToolSetForm;
